<template>
  <div class="app-container">
    <header class="header" v-if="!isLoginPage">
      <div class="logo">
        <fa-icon :icon="['fas', 'graduation-cap']" class="logo-icon" />
        <h1>职行AI - 智慧就业领航系统</h1>
      </div>
      <div class="nav">
        <div
          v-for="tab in tabs"
          :key="tab.key"
          :class="['nav-tab', { active: activeTab === tab.key }]"
          @click="switchTab(tab.key)"
        >
          {{ tab.name }}
        </div>
      </div>
    </header>

    <main class="main-content">
      <router-view />
    </main>

    <footer class="footer" v-if="!isLoginPage">
      <p>
        © {{ new Date().getFullYear() }} 职行AI就业系统 -
        为您提供智能化的就业服务
      </p>
      <div class="social-icons">
        <fa-icon :icon="['fab', 'weixin']" class="social-icon" />
        <fa-icon :icon="['fab', 'weibo']" class="social-icon" />
        <fa-icon :icon="['fab', 'qq']" class="social-icon" />
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

export default {
  name: "AppLayout",
  setup() {
    const router = useRouter();
    const route = useRoute();

    // 检查当前是否为登录页面
    const isLoginPage = computed(() => route.path === '/login');

    // 根据当前路由设置活动标签
    const getActiveTabFromRoute = (path) => {
      if (path === "/") return "home";
      if (path.includes("/job-matching")) return "job-matching";
      if (path.includes("/career-planning")) return "career-planning";
      if (path.includes("/employment-policy")) return "employment-policy";
      if (path.includes("/profile")) return "profile";
      return "home";
    };

    const activeTab = ref(getActiveTabFromRoute(route.path));

    const tabs = reactive([
      { name: "首页", key: "home" },
      { name: "人岗匹配", key: "job-matching" },
      { name: "职业规划", key: "career-planning" },
      { name: "就业政策", key: "employment-policy" },
      { name: "个人中心", key: "profile" },
    ]);

    const switchTab = (tabKey) => {
      activeTab.value = tabKey;

      // 根据tab导航到相应的路由
      const routeMap = {
        home: "/",
        "job-matching": "/job-matching",
        "career-planning": "/career-planning",
        "employment-policy": "/employment-policy",
        "profile": "/profile",
      };

      router.push(routeMap[tabKey] || "/");
    };
    
    // 不再在组件挂载时检查用户登录状态，而是交由路由守卫处理
    onMounted(() => {
      // 这里可以添加其他必要的初始化逻辑
    });

    return {
      tabs,
      activeTab,
      switchTab,
      isLoginPage
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--background-color);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #6978e3;
  color: var(--text-on-primary);
  box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.15);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-icon {
  font-size: 1.8rem;
}

.logo h1 {
  font-size: 1.5rem;
  margin: 0;
}

.nav {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-tab {
  position: relative;
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.85);
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 4px;
  overflow: hidden;
}

.nav-tab:hover {
  color: #ffffff;
  background-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

.nav-tab.active {
  color: #ffffff;
  font-weight: 600;
  background-color: rgba(255, 255, 255, 0.15);
}

.nav-tab.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #ffffff;
  border-radius: 3px 3px 0 0;
  animation: slideIn 0.3s ease forwards;
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.footer {
  background-color: #6978e3;
  color: var(--text-on-primary);
  text-align: center;
  padding: 1.5rem;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.social-icons {
  display: flex;
  gap: 1.5rem;
}

.social-icon {
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.social-icon:hover {
  transform: scale(1.2);
}

/* 响应式样式 */
@media screen and (max-width: 768px) {
  .header {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }

  .nav {
    width: 100%;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .nav-tab {
    font-size: 0.9rem;
    padding: 0.3rem 0;
  }
}
</style>
